import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import { FaChartLine, FaDownload, FaEye, FaExclamationTriangle, FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import { adminApiLogsService } from '../../services/adminApiLogsService';
import { toast } from 'react-toastify';

const ApiLogsTab = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    dateFilter: 'today',
    status: 'all',
    method: 'all',
    start_date: '',
    end_date: '',
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    totalRequests: 0,
    successRate: 0,
    avgResponseTime: 0,
    errorRate: 0,
    todayRequests: 0,
  });

  // Date range presets
  const getDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return {
          start_date: today.toISOString(),
          end_date: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          start_date: yesterday.toISOString(),
          end_date: today.toISOString(),
        };
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          start_date: weekAgo.toISOString(),
          end_date: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        };
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          start_date: monthAgo.toISOString(),
          end_date: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        };
      default:
        return {};
    }
  };

  // Fetch API logs with current filters
  const fetchApiLogs = async (newFilters = null, newPagination = null) => {
    try {
      setLogsLoading(true);
      setError(null);

      const currentFilters = newFilters || filters;
      const currentPagination = newPagination || pagination;

      // Build query parameters
      const queryParams = {
        limit: currentPagination.limit,
        offset: (currentPagination.page - 1) * currentPagination.limit,
      };

      // Add date range based on preset or custom dates
      if (currentFilters.dateFilter !== 'custom') {
        const dateRange = getDateRange(currentFilters.dateFilter);
        Object.assign(queryParams, dateRange);
      } else {
        if (currentFilters.start_date) queryParams.start_date = currentFilters.start_date;
        if (currentFilters.end_date) queryParams.end_date = currentFilters.end_date;
      }

      // Add other filters
      if (currentFilters.status !== 'all') queryParams.status = currentFilters.status;
      if (currentFilters.method !== 'all') queryParams.method = currentFilters.method;

      const response = await adminApiLogsService.getApiLogs(queryParams);
      
      if (response.logs) {
        console.log(response.logs, "logss")
        setApiLogs(response.logs);
        
        // Update pagination if total count is provided
        if (response.total !== undefined) {
          setPagination(prev => ({
            ...prev,
            total: response.total,
          }));
        }

        // Calculate statistics from current data
        calculateStatistics(response.logs);
      } else {
        setApiLogs([]);
      }
    } catch (error) {
      console.error('Error fetching API logs:', error);
      setError('Failed to load API logs');
      toast.error('Failed to load API logs');
    } finally {
      setLogsLoading(false);
      setLoading(false);
    }
  };

  // Calculate statistics from logs data
  const calculateStatistics = (logs) => {
    if (!logs || logs.length === 0) {
      setStatistics({
        totalRequests: 0,
        successRate: 0,
        avgResponseTime: 0,
        errorRate: 0,
        todayRequests: 0,
      });
      return;
    }

    const total = logs.length;
    const successful = logs.filter(log => log.status_code >= 200 && log.status_code < 300).length;
    const errors = logs.filter(log => log.status_code >= 400).length;
    const avgResponseTime = logs.reduce((sum, log) => sum + (log.response_time || 0), 0) / total;

    // Calculate today's requests
    const today = new Date().toDateString();
    const todayRequests = logs.filter(log => 
      new Date(log.created_at).toDateString() === today
    ).length;

    setStatistics({
      totalRequests: total,
      successRate: total > 0 ? ((successful / total) * 100).toFixed(1) : 0,
      avgResponseTime: Math.round(avgResponseTime),
      errorRate: total > 0 ? ((errors / total) * 100).toFixed(1) : 0,
      todayRequests,
    });
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Reset pagination to first page
    const newPagination = { ...pagination, page: 1 };
    setPagination(newPagination);
    
    // Fetch data with new filters
    fetchApiLogs(newFilters, newPagination);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    const newPagination = { ...pagination, page: newPage };
    setPagination(newPagination);
    fetchApiLogs(null, newPagination);
  };

  // Refresh data
  const refreshData = () => {
    fetchApiLogs();
  };

  // Badge and color helpers
  const getStatusBadge = (status) => {
    const statusStr = status.toString();
    if (statusStr.startsWith('2')) {
      return <Badge bg="success">{status}</Badge>;
    } else if (statusStr.startsWith('4')) {
      return <Badge bg="warning">{status}</Badge>;
    } else if (statusStr.startsWith('5')) {
      return <Badge bg="danger">{status}</Badge>;
    } else {
      return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getMethodBadge = (method) => {
    const variants = {
      'GET': 'primary',
      'POST': 'success',
      'PUT': 'warning',
      'DELETE': 'danger'
    };
    return <Badge bg={variants[method] || 'secondary'}>{method}</Badge>;
  };

  const getResponseTimeColor = (time) => {
    if (time < 100) return 'success';
    if (time < 300) return 'warning';
    return 'danger';
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    console.log(log)
    setShowLogModal(true);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatEndpointDisplay = (endpoint) => {
    if (!endpoint) return '—';
    const [path, query = ''] = endpoint.split('?');
    if (!query) return path;

    const params = new URLSearchParams(query);
    params.delete('apiKey');
    params.delete('api_key');
    const rest = params.toString();
    return rest ? `${path}?${rest}` : path;
  };

  // Initial data fetch
  useEffect(() => {
    fetchApiLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading API logs...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4 api-logs-tab">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
          <h2 className="mb-3">API Logs & Usage Monitoring</h2>
          <p className="text-muted">Monitor API usage, track performance, and analyze client activity.</p>
            </div>
            <Button variant="outline-primary" onClick={refreshData} disabled={logsLoading}>
              <FaSyncAlt className={`me-2 ${logsLoading ? 'fa-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Usage Statistics */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-primary h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title text-muted">Total Requests</h6>
                  <h3 className="mb-1">{statistics.totalRequests.toLocaleString()}</h3>
                  <p className="text-muted mb-0">in selected period</p>
                </div>
                <div className="text-primary fs-1">
                  <FaChartLine />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-success h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title text-muted">Success Rate</h6>
                  <h3 className="mb-1">{statistics.successRate}%</h3>
                  <p className="text-muted mb-0">successful requests</p>
                </div>
                <div className="text-success fs-1">
                  <FaCheckCircle />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-warning h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title text-muted">Avg Response Time</h6>
                  <h3 className="mb-1">{statistics.avgResponseTime}ms</h3>
                  <p className="text-muted mb-0">average latency</p>
                </div>
                <div className="text-warning fs-1">
                  <FaChartLine />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-danger h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title text-muted">Error Rate</h6>
                  <h3 className="mb-1">{statistics.errorRate}%</h3>
                  <p className="text-muted mb-0">failed requests</p>
                </div>
                <div className="text-danger fs-1">
                  <FaExclamationTriangle />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <Form.Select
              value={filters.dateFilter}
              onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="200">200 (Success)</option>
              <option value="401">401 (Unauthorized)</option>
              <option value="429">429 (Rate Limited)</option>
              <option value="500">500 (Server Error)</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Method</Form.Label>
            <Form.Select
              value={filters.method}
              onChange={(e) => handleFilterChange('method', e.target.value)}
            >
              <option value="all">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Custom Date Range */}
      {filters.dateFilter === 'custom' && (
      <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </Form.Group>
        </Col>
      </Row>
      )}

      {/* API Logs Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  API Logs ({apiLogs.length})
                  {logsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
                </h5>
                <Button variant="outline-secondary" size="sm">
                  <FaDownload className="me-2" />
                  Export
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {apiLogs.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No API logs found for the selected criteria.</p>
                </div>
              ) : (
              <div className="api-logs-table-wrap">
              <Table striped hover className="api-logs-table mb-0">
                <thead>
                  <tr>
                    <th className="col-timestamp">Timestamp</th>
                    <th className="col-user">User</th>
                    <th className="col-endpoint">Endpoint</th>
                    <th className="col-method">Method</th>
                    <th className="col-status">Status</th>
                    <th className="col-response">Response Time</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                    {apiLogs.map((log) => (
                    <tr key={log.id}>
                        <td className="col-timestamp">{formatTimestamp(log.created_at)}</td>
                        <td className="col-user">
                          <span className="api-logs-user" title={log.company_name || log.user_email || 'Unknown'}>
                            {log.company_name || log.user_email || 'Unknown'}
                          </span>
                        </td>
                      <td className="col-endpoint">
                        <code className="api-logs-endpoint" title={log.endpoint}>
                          {formatEndpointDisplay(log.endpoint)}
                        </code>
                      </td>
                      <td className="col-method">{getMethodBadge(log.method)}</td>
                        <td className="col-status">{getStatusBadge(log.status_code)}</td>
                      <td className="col-response">
                          <span className={`text-${getResponseTimeColor(log.response_time)}`}>
                            {log.response_time || 0}ms
                        </span>
                      </td>
                      <td className="col-actions">
                        <Button
                          variant="outline-info"
                          size="sm"
                          title="View Details"
                          onClick={() => handleViewLog(log)}
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <Row className="mt-3">
          <Col>
            <nav className="d-flex justify-content-center">
              <ul className="pagination">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: Math.ceil(pagination.total / pagination.limit) })
                  .slice(
                    Math.max(0, pagination.page - 3),
                    Math.min(Math.ceil(pagination.total / pagination.limit), pagination.page + 2)
                  )
                  .map((_, index) => {
                    const pageNum = Math.max(0, pagination.page - 3) + index + 1;
                    return (
                      <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                
                <li className={`page-item ${pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </Col>
        </Row>
      )}

      {/* API Log Details Modal */}
      <Modal show={showLogModal} onHide={() => setShowLogModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>API Log Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <Row>
              <Col md={6}>
                <h6>Request Information</h6>
                <p><strong>Endpoint:</strong></p>
                <code className="api-logs-endpoint-full">{selectedLog.endpoint}</code>
                <p><strong>Method:</strong> {getMethodBadge(selectedLog.method)}</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedLog.status_code)}</p>
                <p><strong>Response Time:</strong> 
                  <span className={`text-${getResponseTimeColor(selectedLog.response_time)}`}>
                    {selectedLog.response_time || 0}ms
                  </span>
                </p>
                <p><strong>Timestamp:</strong> {formatTimestamp(selectedLog.created_at)}</p>
              </Col>
              <Col md={6}>
                <h6>User Information</h6>
                <p><strong>User:</strong> { selectedLog.user_email || ''}</p>
                <p><strong>API Limit Before:</strong> {selectedLog.api_limit_before || 'N/A'}</p>
                <p><strong>API Limit After:</strong> {selectedLog.api_limit_after || 'N/A'}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ApiLogsTab; 